import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldCheck, Eye, Archive, ArrowUpRight } from 'lucide-react';
import { SUPPLIERS, BRAND_ASSOC_STATUSES } from '../data/mockData';
import SEO from '../components/SEO';
import Img from '../components/Img';
import { EDITORIAL_HEROES } from '../data/heroImages';

const TIER_PILL_CLASS = {
  'Verified': 'giwahs-tier-pill giwahs-tier-verified',
  'Publicly Reported': 'giwahs-tier-pill giwahs-tier-reported',
  'Unverified': 'giwahs-tier-pill giwahs-tier-unverified',
  'Historical': 'giwahs-tier-pill giwahs-tier-historical',
};

const tierPillClass = (status) => TIER_PILL_CLASS[status] || 'giwahs-tier-pill giwahs-tier-historical';

const statusBadgeClass = (status) => {
  const s = BRAND_ASSOC_STATUSES.find(s => s.key === status);
  if (!s) return 'border-black';
  switch (s.tone) {
    case 'success': return 'bg-black text-white border-black';
    case 'neutral': return 'bg-white text-black border-black';
    case 'warning': return 'bg-amber-50 text-amber-900 border-amber-900';
    case 'muted': return 'bg-neutral-100 text-neutral-600 border-neutral-400';
    default: return 'border-black';
  }
};

const STATUS_ICONS = {
  Verified: ShieldCheck,
  'Publicly Reported': Eye,
  Unverified: AlertTriangle,
  Historical: Archive,
};

const BrandFactoryIntelligence = () => {

  // Flatten brand associations across all suppliers
  const allAssoc = SUPPLIERS.flatMap(s =>
    s.knownBrandAssociations.map(a => ({ ...a, supplier: s }))
  );

  const byStatus = (status) => allAssoc.filter(a => a.status === status);

  return (
    <div data-testid="intelligence-page" className="surface-obsidian text-platinum">
      <SEO
        title="Brand × Factory Intelligence — Verified Sourcing Brief"
        description="Brand-to-factory associations categorised as Verified, Publicly Reported, Unverified, or Historical. Sourcing intelligence brief for fashion buyers."
      />
      <section className="relative overflow-hidden border-b border-black bg-[#0A0A0A] text-white grain-overlay">
        <div className="absolute inset-0">
          <Img src={EDITORIAL_HEROES.intelligence.src} alt={EDITORIAL_HEROES.intelligence.alt} variant="hero" priority className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-28">
          <span className="label-overline" style={{ color: '#C8A96B' }}>/ Brand × Factory Intelligence</span>
          <h1 className="mt-5 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 6.5rem)', lineHeight: 0.92 }}>
            Brand-to-factory<br /><em className="giwahs-gold-emphasis" style={{ fontStyle: 'italic' }}>Intelligence Brief.</em>
          </h1>
          <p className="mt-7 max-w-xl text-base text-neutral-300 md:text-lg">
            Finding the right manufacturing partner isn't always straightforward. GIWAHS does the hard work of organising and assessing supplier information, so you can focus on building your business with confidence.
          </p>
        </div>
      </section>

      {/* Status framework */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ Status Framework</span>
          <h2 className="mt-4 font-display text-4xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>Four-tier verification.</h2>

          <div className="mt-10 grid gap-px bg-black md:grid-cols-2 lg:grid-cols-4">
            {BRAND_ASSOC_STATUSES.map(s => {
              const Icon = STATUS_ICONS[s.key];
              return (
                <div key={s.key} className="giwahs-pillar-card p-7" data-testid={`status-tier-${s.key.toLowerCase().replace(/\s+/g, '-')}`}>
                  <Icon size={22} strokeWidth={1.5} className="text-[#D6B24A]" />
                  <div className="mt-5">
                    <span className={tierPillClass(s.key)}>{s.key}</span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-platinum-2">{s.description}</p>
                  <div className="mt-5 font-mono-data text-xs text-[#F0C85A]">
                    {byStatus(s.key).length} records indexed
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Legal disclaimer */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <div className="border border-amber-900 bg-amber-50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <AlertTriangle size={22} strokeWidth={1.5} className="mt-1 shrink-0 text-amber-900" />
              <div className="text-sm leading-relaxed text-amber-900 md:text-base">
                <strong className="font-semibold">Legal & editorial disclaimer.</strong> All brand-to-factory association data shown on Giwahs is for sourcing intelligence purposes only. <strong>Giwahs does not claim that any factory currently produces for any high-end or well-known brand unless explicitly marked Verified through legally sourced, publicly available, and reviewable documentation.</strong> Unverified and Publicly Reported entries are surfaced as context — not as endorsements or confirmations. Historical entries refer to past relationships that may no longer be active. Users are responsible for conducting their own independent due diligence before contracting with any supplier. <em>Entries shown during this preview period are illustrative reference records; verified records are marked accordingly.</em>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence Brief table */}
      <section className="surface-obsidian text-platinum">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ Intelligence Brief</span>
          <h2 className="mt-4 font-display text-4xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
            {allAssoc.length} brand association records.
          </h2>
          <p className="mt-3 font-mono-data text-xs text-neutral-500">ILLUSTRATIVE DATA · WEEKLY UPDATES SCHEDULED IN PRODUCTION</p>

          <div className="mt-10 border border-black">
            <div className="hidden grid-cols-12 gap-3 border-b border-hairline surface-obsidian-2 px-5 py-3 label-overline md:grid">
              <div className="col-span-3">Brand</div>
              <div className="col-span-4">Factory</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Period</div>
              <div className="col-span-1 text-right">Open</div>
            </div>
            {allAssoc.map((a, i) => (
              <div key={i} data-testid={`dossier-row-${i}`} className="grid grid-cols-1 gap-3 border-b border-hairline px-5 py-4 last:border-b-0 md:grid-cols-12 md:items-center">
                <div className="md:col-span-3">
                  <div className="font-display text-lg tracking-tight text-platinum" style={{ fontWeight: 600 }}>{a.brand}</div>
                  <div className="font-mono-data text-[10px] text-muted-warm md:hidden">{a.period}</div>
                </div>
                <div className="md:col-span-4">
                  <Link to={`/supplier/${a.supplier.slug}`} className="giwahs-view-link text-sm">{a.supplier.name}</Link>
                  <div className="font-mono-data text-[10px] text-muted-warm">{a.supplier.city}, {a.supplier.country} · {a.supplier.type}</div>
                </div>
                <div className="md:col-span-2">
                  <span className={tierPillClass(a.status)}>{a.status}</span>
                </div>
                <div className="hidden font-mono-data text-xs giwahs-period-value md:col-span-2 md:block">{a.period}</div>
                <div className="md:col-span-1 md:text-right">
                  <Link to={`/supplier/${a.supplier.slug}`} className="giwahs-view-link inline-flex items-center gap-1 label-overline">
                    View <ArrowUpRight size={11} strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandFactoryIntelligence;
